import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { pink } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import Modal from 'react-bootstrap/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useForm, Controller } from 'react-hook-form';
import { useParams , useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert'
import BreadcrumbsWrapper from '../../components/breadcrumbs'
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';


function MyVerticallyCenteredModal({ show, onHide, authToken, onCustomerAdd }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCustomer = async () => {
    // Check if at least email or phone is provided
    if (!searchFields.email && !searchFields.phone_number) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please provide at least an email or phone number to create a customer.'
      });
      return;
    }

    setCreating(true);
    try {
      const payload = {
        title: searchFields.title || '',
        forename: searchFields.forename || '',
        surname: searchFields.surname || '',
        email: searchFields.email || '',
        phone_number: searchFields.phone_number || ''
      };

      const res = await postRequest('private/createCustomer', payload, authToken);
      if ((res?.status === 'success' || res?.status === 200) && res?.data) {
        Swal.fire({
          icon: 'success',
          title: 'Customer Created',
          text: 'Customer has been created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
        onCustomerAdd(res.data);
        onHide();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res?.message || 'Failed to create customer'
        });
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.message || 'Something went wrong'
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Customers</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className='text-center'>
                      <div style={{ padding: '20px' }}>
                        <div style={{ marginBottom: '15px', color: '#666' }}>
                          No customers found
                        </div>
                        {(searchFields.email || searchFields.phone_number) && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateCustomer}
                            disabled={creating}
                            sx={{
                              backgroundColor: '#6f42c1',
                              '&:hover': {
                                backgroundColor: '#5a32a3'
                              }
                            }}
                          >
                            {creating ? 'Creating...' : 'ADD CUSTOMER'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}
export const  LastingPowersOfAttorneys=()=> {

  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(true);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
    const [modalShow, setModalShow] = React.useState(false);
    const [age, setAge] = React.useState('');
      const [customerData, setCustomerData] = useState(null);
      const [groupCodes, setGroupCodes] = useState([]);
       const [customerError, setCustomerError] = useState(false);
       const [selectedDocuments, setSelectedDocuments] = useState([]);
       const [lenderPanels, setLenderPanels] = useState([]);
       const [partners, setPartners] = useState([]);


      
        const { customerId, slug } = useParams();
          const location = useLocation()
          const navigate = useNavigate();
        //  const authCtx = useContext(AuthContext);
           const productId = location.state?.productId;

  // Get customerId from URL query params
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customerId');

                 const slugToTitle = (slug) => {
  return slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  const readableTitle = slugToTitle(slug || '');


  // Search states
  const [searchFields, setSearchFields] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
  });

   const {
      handleSubmit,
      control,
      reset,
      formState: { errors },
    } = useForm({
      defaultValues: {
        group_code: '',
        acquisition: 'Inbound',
      //net_value: ''
        lender_panel_id: '',
        partner_id: '',
      },
      mode: 'onTouched',
    });

  const authCtx = useContext(AuthContext);
  const debounceRef = useRef();

  const handleCheckboxChange = (event) => {
  const { name, checked } = event.target;
  setSelectedDocuments((prev) =>
    checked ? [...prev, name] : prev.filter((doc) => doc !== name)
  );
};

  // Fetch customers
  const fetchCustomers = async (payloadFields = searchFields, pageVal = page) => {
    const payload = {
      ...payloadFields,
      page: pageVal,
      limit: 5,
    };

    setLoading(true);
    try {
      const response = await postRequest('private/customerList', payload, authCtx.token);
      if (response?.data) {
        setCustomerList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setCustomerList([]);
        setTotalPages(1);
      }
    } catch (err) {
      setCustomerList([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // On initial load and page change
  useEffect(() => {
    fetchCustomers(searchFields, page);
    // eslint-disable-next-line
  }, [page]);

  // Debounced search on fields change
  useEffect(() => {
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers(searchFields, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchFields]);

  // Handle individual search input change


  const handlePageChange = (event, value) => {
    setPage(value);
  };


 const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setGroupCodes(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch group codes:", err);
    }
  };

  // Fetch active lender panels
  const fetchActiveLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
      }
    } catch (error) {
      console.error('Error fetching lender panels:', error);
    }
  };

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await postRequest('private/partnerList', { page: 1, limit: 1000 }, authCtx.token);
      if (response?.status === 200 && response?.data?.docs) {
        setPartners(response.data.docs);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

    const handleCustomerAdd = (customer) => {
    if (!selectedCustomers.some(c => c._id === customer._id)) {
      setSelectedCustomers(prev => [...prev, customer]);
      setCustomerError(false); // ✅ reset error
    }
  };

  const fetchCustomerDetails = async (id) => {
    try {
      const response = await postRequest(`private/getCustomerIndividualData/${id}`, {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setCustomerData(response.data);
      }
    } catch (err) {
      console.error('Error fetching customer details', err);
    }
  };

  useEffect(() => {
    fetchGroupCodes();
    if (customerId) fetchCustomerDetails(customerId);
  }, [customerId]);

  // Fetch active lender panels and partners on mount
  useEffect(() => {
    fetchActiveLenderPanels();
    fetchPartners();
  }, [authCtx.token]);

  // Fetch initial customer from URL and add to selectedCustomers
  useEffect(() => {
    const fetchInitialCustomer = async () => {
      if (customerIdFromUrl && authCtx.token && selectedCustomers.length === 0) {
        console.log('Fetching initial customer from URL:', customerIdFromUrl);
        try {
          const customerRes = await postRequest(`private/getCustomerIndividualData/${customerIdFromUrl}`, {}, authCtx.token);
          if (customerRes?.data) {
            console.log('Fetched initial customer:', customerRes.data);
            setSelectedCustomers([customerRes.data]);
            setCustomerError(false);
          }
        } catch (err) {
          console.error('Error fetching initial customer:', err);
        }
      }
    };

    fetchInitialCustomer();
  }, [customerIdFromUrl, authCtx.token]);


    const onSubmit = async (formData) => {
     
      if (selectedCustomers.length === 0) {
        setCustomerError(true);
        return;
      }
  
      const created_id = localStorage.getItem('id')
  
      const payload = {
        ...formData,
        product_id:productId,
        created_id:created_id,
        customer_ids: selectedCustomers.map(c => c._id),
        documents: selectedDocuments,
        lender_panel_id: formData.lender_panel_id || null,
        partner_id: formData.partner_id || null,
      };
  
      try {
        const res = await postRequest('private/createLeads', payload, authCtx.token);
        if (res?.status == 'success') {
          Swal.fire('Success', 'Leads created successfully!', 'success');
          reset();
          setSelectedCustomers([]);
          navigate('/leads');
        } else {
          Swal.fire('Error', res?.message || 'Failed to create leads', 'error');
        }
      } catch (err) {
        Swal.fire('Error', err?.message || 'Something went wrong', 'error');
      }
    };



  return (
    <>
       <BreadcrumbsWrapper
               items={[
               { label: 'Dashboard', path: '/dashboard' },
                { label: 'Customers', path: '/customers' },
                { label: 'Lasting Powers Attorney', path: '' },
                  ]}
                    />
                   <br></br>
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Create {readableTitle}</h1>
      </div>

      {/* Search Form */}
      <div className='page-top-formview mb-3'>
       <Form onSubmit={handleSubmit(onSubmit)}>
         <Row>
                  <Col xs={6}>
                    <h5>Group Code</h5>
                    <Controller
                      name="group_code"
                      control={control}
                      rules={{ required: 'Group Code is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth sx={{ my: 1 }} error={!!errors.group_code}>
                          <InputLabel>Group Code</InputLabel>
                          <Select {...field} label="Group Code">
                            {groupCodes.map((item) => (
                              <MenuItem key={item.code} value={item._id}>
                                {item.name} ({item.code})
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.group_code && (
                            <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.group_code.message}</p>
                          )}
                        </FormControl>
                      )}
                    />
                  </Col>
      
                  <Col xs={6}>
                    <h5>Acquisition</h5>
                    <Controller
                      name="acquisition"
                      control={control}
                      rules={{ required: 'Acquisition is required' }} // ✅ Added rule
                      render={({ field }) => (
                        <FormControl fullWidth sx={{ my: 1 }} error={!!errors.acquisition}>
                          <InputLabel>Acquisition</InputLabel>
                          <Select {...field} label="Acquisition">
                            <MenuItem value='Inbound'>Inbound</MenuItem>
                            <MenuItem value='Cold-Acquistion'>Cold-Acquistion</MenuItem>
                          </Select>
                          {errors.acquisition && (
                            <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.acquisition.message}</p>
                          )}
                        </FormControl>
                      )}
                    />
                  </Col>

                </Row>

          {/* Lender Panel and Partner Section */}
          <Row className="mt-3">
            <Col xs={12} md={6}>
              <h5>Lender Panel</h5>
              <Controller
                name="lender_panel_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ my: 1 }}>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{ backgroundColor: 'white' }}
                    >
                      <MenuItem value="">
                        <em>Select Lender Panel (Optional)</em>
                      </MenuItem>
                      {lenderPanels.map((panel) => (
                        <MenuItem key={panel._id} value={panel._id}>
                          {panel.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Col>
            <Col xs={12} md={6}>
              <h5>Partner</h5>
              <Controller
                name="partner_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ my: 1 }}>
                    <Select
                      {...field}
                      displayEmpty
                      sx={{ backgroundColor: 'white' }}
                    >
                      <MenuItem value="">
                        <em>Select Partner (Optional)</em>
                      </MenuItem>
                      {partners.map((partner) => (
                        <MenuItem key={partner._id} value={partner._id}>
                          {partner.name || 'Unknown'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3 mt-3">
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Customers
                  </Typography>
                  {customerError && (
                    <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                      Please select at least one customer.
                    </Alert>
                  )}
                  <Box sx={{ mb: 3 }}>
                    {selectedCustomers.length > 0 ? (
                      selectedCustomers.map((customer) => (
                        <Box
                          key={customer._id}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            mb: 2,
                            p: 1.5,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#f9f9f9'
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {customer.title} {customer.forename} {customer.surname}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {customer.email && (
                                <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                                  📧 {customer.email}
                                </Typography>
                              )}
                              {customer.phone_number && (
                                <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                                  📞 {customer.phone_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            onClick={() =>
                              setSelectedCustomers(prev =>
                                prev.filter(c => c._id !== customer._id)
                              )
                            }
                            sx={{
                              minWidth: 'auto',
                              color: '#d32f2f',
                              '&:hover': {
                                backgroundColor: '#ffebee'
                              }
                            }}
                          >
                            ✕
                          </Button>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          No customers added yet. Click "Add Customer" to add customers.
                        </Typography>
                      </Box>
                    )}
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={() => setModalShow(true)}
                      sx={{ color: '#bb0000', mt: 1 }}
                    >
                      Add Customer
                    </Button>
                  </Box>
                  <MyVerticallyCenteredModal
                    show={modalShow}
                    onHide={() => setModalShow(false)}
                    authToken={authCtx.token}
                    onCustomerAdd={handleCustomerAdd}
                  />
                </Row>

          <Row className="mb-3">
             <h5>Documents</h5>
            
        
<FormControlLabel
  control={
    <Checkbox
      name="Health & Welfare"
      checked={selectedDocuments.includes("Health & Welfare")}
      onChange={handleCheckboxChange}
      sx={{
        '&.Mui-checked': { color: '#1976d2' },
      }}
    />
  }
  label="Health & Welfare"
/>

<FormControlLabel
  control={
    <Checkbox
      name="Property & Finance"
      checked={selectedDocuments.includes("Property & Finance")}
      onChange={handleCheckboxChange}
      sx={{
        '&.Mui-checked': { color: '#1976d2' },
      }}
    />
  }
  label="Property & Finance"
/>


  
          
            </Row>
           
            <Row>
           
            <Form.Group as={Col} controlId="searchId">
              
               <FormControl sx={{ my: 1, width: '100%' }} variant="outlined" >
                                <Stack spacing={2} direction="row">
                                
                                <Button variant="contained" type="submit" className='red-bg red-button text-capitalize h-56'>Submit</Button>

                            </Stack>
                                </FormControl>
            </Form.Group>


          </Row>
        </Form> 
         


      </div>

      {/* Table View */}
      <div>
        

        {/* Pagination */}
        {customerList.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              size="medium"
              sx={{
                '& .Mui-selected': {
                  backgroundColor: '#bb0000 !important',
                  color: '#fff !important',
                },
                '& .MuiPaginationItem-root': {
                  color: '#bb0000',
                  borderColor: '#bb0000',
                },
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
